import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import { Link, usePage } from '@inertiajs/react'
import { AppLayout } from './AppLayout'

interface SidebarItem {
  label: string
  href: string
  adminOnly?: boolean
}

const SECTIONS: Record<string, SidebarItem[]> = {
  manage: [
    { label: 'Projects', href: '/manage/projects' },
    { label: 'Webhooks', href: '/manage/webhooks/webhooks' },
    { label: 'Cron Jobs', href: '/manage/cron_jobs' },
    { label: 'Approvals', href: '/manage/approvals', adminOnly: true },
    { label: 'Agents', href: '/manage/agents', adminOnly: true },
    { label: 'Connections', href: '/manage/agents/connection_requests', adminOnly: true },
    { label: 'Secrets', href: '/manage/secrets', adminOnly: true },
    { label: 'Skills', href: '/manage/skills', adminOnly: true },
    { label: 'Tools', href: '/manage/tools', adminOnly: true },
    { label: 'Runners', href: '/manage/runners', adminOnly: true },
    { label: 'Claude Auth', href: '/manage/claude_authorizations', adminOnly: true },
  ],
  monitoring: [
    { label: 'Dashboard', href: '/monitoring/dashboard' },
    { label: 'Agent Status', href: '/monitoring/agent_statuses' },
    { label: 'Session Logs', href: '/monitoring/session_logs' },
    { label: 'Webhook Events', href: '/monitoring/webhook_events' },
    { label: 'Statistics', href: '/monitoring/statistics' },
  ],
}

interface Props {
  section: 'manage' | 'monitoring'
  title?: string
  action?: React.ReactNode
  children: React.ReactNode
}

export function SidebarLayout({ section, title, action, children }: Props) {
  const { current_agent } = usePage().props
  const currentPath = (usePage().url || '').split('?')[0]
  const items = SECTIONS[section] || []
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = useState(false)

  function isActive(href: string) {
    return currentPath.startsWith(href)
  }

  const visibleItems = items.filter(item => !item.adminOnly || current_agent?.admin)

  const sidebarContent = (
    <List sx={{ pt: 2 }}>
      {visibleItems.map((item) => (
        <ListItemButton
          key={item.href}
          component={Link}
          href={item.href}
          selected={isActive(item.href)}
          onClick={() => isMobile && setDrawerOpen(false)}
          sx={{
            mx: 1,
            borderRadius: 1,
            minHeight: 44,
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: isActive(item.href) ? 600 : 400,
            }}
          />
        </ListItemButton>
      ))}
    </List>
  )

  return (
    <AppLayout noPadding>
      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            {sidebarContent}
          </Box>
        )}

        {/* Mobile sidebar drawer */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: 220 } }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {section === 'manage' ? 'Manage' : 'Monitoring'}
              </Typography>
            </Box>
            {sidebarContent}
          </Drawer>
        )}

        <Box sx={{ flexGrow: 1, py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 } }}>
          <Container maxWidth={false} disableGutters>
            {title && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isMobile && (
                    <IconButton onClick={() => setDrawerOpen(true)} edge="start" sx={{ minWidth: 44, minHeight: 44 }}>
                      <MenuIcon />
                    </IconButton>
                  )}
                  <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>{title}</Typography>
                </Box>
                {action}
              </Box>
            )}
            {!title && isMobile && (
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ mb: 1, minWidth: 44, minHeight: 44 }}>
                <MenuIcon />
              </IconButton>
            )}
            {children}
          </Container>
        </Box>
      </Box>
    </AppLayout>
  )
}
